import { isBefore, parseISO } from 'date-fns';
import type { Privacy } from '../privacy';
import type { ProgramEventType } from '../program-event';
import type { ProgramDetailDataObject } from '../service';
import type { Tag } from '../tag';
import { type Topic, createTopics } from '../topic';
import { type UploadedFile, createUploadedFile } from '../uploaded-file';
import { type AutocompleteUser, type UserList, createAutocompleteUser, createUserList } from '../user';

export type Program = {
  administrators?: AutocompleteUser[];
  author: UserList;
  authorLogo: { id: string; url: string };
  authorName: string;
  authorOverviewText?: string;
  coverPhoto?: UploadedFile;
  endTime: Date;
  expectedOutcomes?: string[];
  featuredParticipants?: ProgramFeaturedParticipant[];
  htmlAuthorOverview?: string;
  htmlContent?: string;
  id: string;
  instructors?: AutocompleteUser[];
  mandatory: boolean;
  name: string;
  organization?: { id: string };
  price?: number;
  priceOption: 'contact_us' | 'free' | 'paid';
  privacy: Privacy;
  profilePhoto?: UploadedFile;
  requestedToJoin?: boolean;
  settings: {
    allowComments: boolean;
    allowedEventsAdmin: ProgramEventType[];
    allowedEventsMember: ProgramEventType[];
    allowInvitees: boolean;
    allowPosts: boolean;
    allowUnsubscribe: boolean;
    viewMembers?: boolean;
  };
  sponsors?: ProgramSponsor[];
  startTime: Date;
  status: 'draft' | 'published';
  tags?: Tag[];
  textContent?: string;
  topics: Topic[];
  users?: AutocompleteUser[];
  whoShouldEnroll?: string;
  withingsDevices?: string[];
};

export type ProgramFeaturedParticipant = {
  description: string;
  htmlDescription: string;
  id: string;
  logo?: { id: string; url: string };
  name: string;
};

export type ProgramSponsor = {
  description: string;
  htmlDescription: string;
  id: string;
  logo?: { id: string; url: string };
  name: string;
  price: number;
};

export const createProgram = (data: ProgramDetailDataObject): Program => ({
  administrators: data.administrators?.map((administrator) => createAutocompleteUser(administrator)),
  author: createUserList(data.author),
  authorLogo: data.author_logo,
  authorName: data.author_name,
  authorOverviewText: data.author_overview_text,
  coverPhoto: data.cover_photo ? createUploadedFile(data.cover_photo) : undefined,
  endTime: parseISO(data.end_time),
  expectedOutcomes: data.expected_outcomes,
  featuredParticipants: data.featured_participants?.map((featuredParticipant) => ({
    description: featuredParticipant.description,
    htmlDescription: featuredParticipant.html_description,
    id: featuredParticipant.id,
    logo: featuredParticipant.logo,
    name: featuredParticipant.name,
  })),
  htmlAuthorOverview: data.html_author_overview,
  htmlContent: data.html_content,
  id: data.id,
  instructors: data.instructors?.map((instructor) => createAutocompleteUser(instructor)),
  mandatory: data.mandatory,
  name: data.name,
  organization: data.organization,
  price: data.price,
  priceOption: data.price_option,
  privacy: data.privacy,
  profilePhoto: data.profile_photo ? createUploadedFile(data.profile_photo) : undefined,
  requestedToJoin: data.requested_to_join,
  settings: {
    allowComments: data.settings.allow_comments,
    allowedEventsAdmin: data.settings.allowed_events_admin,
    allowedEventsMember: data.settings.allowed_events_member,
    allowInvitees: data.settings.allow_invitees,
    allowPosts: data.settings.allow_posts,
    allowUnsubscribe: data.settings.allow_unsubscribe,
    viewMembers: data.settings.view_members,
  },
  sponsors: data.sponsors?.map((sponsor) => ({
    description: sponsor.description,
    htmlDescription: sponsor.html_description,
    id: sponsor.id,
    logo: sponsor.logo,
    name: sponsor.name,
    price: sponsor.price,
  })),
  startTime: parseISO(data.start_time),
  status: data.status,
  tags: data.tags,
  textContent: data.text_content,
  topics: createTopics(data.topics),
  users: data.users?.map((user) => createAutocompleteUser(user)),
  whoShouldEnroll: data.who_should_enroll,
  withingsDevices: data.withings_devices,
});

export const getMinContentDate = ({ startTime }: { startTime?: Date }) =>
  startTime && isBefore(new Date(), startTime) ? startTime : new Date();
